import React, { useEffect } from 'react';
// Toaster removed - using global Toaster from App.js
import { Skeleton } from './ui/skeleton';
import { useConsultantDashboard } from '../hooks/useConsultantDashboard/useConsultantDashboard';
import ConsultantDashboardHeader from './ConsultantDashboard/ConsultantDashboardHeader';
import ConsultantDashboardErrorBanner from './ConsultantDashboard/ConsultantDashboardErrorBanner';
import ConsultantDashboardEmptyState from './ConsultantDashboard/ConsultantDashboardEmptyState';
import ConsultantDashboardStats from './ConsultantDashboard/ConsultantDashboardStats';
import ConsultantDashboardPendingRequestsBanner from './ConsultantDashboard/ConsultantDashboardPendingRequestsBanner';
import ConsultantDashboardQuickActions from './ConsultantDashboard/ConsultantDashboardQuickActions';
import ConsultantDashboardSessionTabs from './ConsultantDashboard/ConsultantDashboardSessionTabs';
import AssignedUsersTab from './ConsultantDashboard/AssignedUsersTab';
import EngagementsTab from './ConsultantDashboard/EngagementsTab';
import CompletedUsersTab from './ConsultantDashboard/CompletedUsersTab';
import ActiveSessionsTab from './ConsultantDashboard/ActiveSessionsTab';

const ConsultantDashboard = () => {
  const {
    user,
    sessions,
    stats,
    activeTab,
    setActiveTab,
    loading,
    error,
    mobileMenuOpen,
    setMobileMenuOpen,
    completedUsers,
    expandedUser,
    userAssessments,
    loadingAssessments,
    assignedUsers,
    showSessionModal,
    setShowSessionModal,
    selectedUser,
    selectedAssessmentType,
    setSelectedAssessmentType,
    startingSession,
    openingModal,
    openingModalUserId,
    majorIndustries,
    availableSubIndustries,
    selectedMajorIndustry,
    selectedMajorIndustryObj,
    selectedSubIndustry,
    industriesLoading,
    companyName,
    setCompanyName,
    annualEnergySpend,
    setAnnualEnergySpend,
    facilitySize,
    setFacilitySize,
    contactName,
    setContactName,
    contactEmail,
    setContactEmail,
    state,
    setState,
    sessionNotes,
    setSessionNotes,
    loadRealData,
    handleStartNewSession,
    handleResumeSession,
    handleViewResults,
    toggleUserAssessments,
    handleStartSessionClick,
    handleStartSession,
    handleMajorIndustryChange,
    handleSubIndustryChange,
    getStatusBadge,
    formatTime
  } = useConsultantDashboard();

  // Auto-set assessment type when selectedUser changes and has pending request
  useEffect(() => {
    if (selectedUser) {
      const hasPending = selectedUser.has_pending_request || selectedUser.has_pending_session;
      if (hasPending && selectedUser.pending_session?.assessment_type) {
        const requestedType = selectedUser.pending_session.assessment_type.toUpperCase();
        if (selectedAssessmentType !== requestedType) {
          setSelectedAssessmentType(requestedType);
        }
      }
    }
  }, [selectedUser, selectedAssessmentType, setSelectedAssessmentType]);


  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Skeleton className="h-8 w-48" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Stats Grid Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </div>
            ))}
          </div>
          {/* Recent Activity Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <Skeleton className="h-6 w-48 mb-4" />
              {[1, 2, 3].map((i) => (
                <div key={i} className="mb-4">
                  <Skeleton className="h-5 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ))}
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <Skeleton className="h-6 w-48 mb-4" />
              {[1, 2, 3].map((i) => (
                <div key={i} className="mb-4">
                  <Skeleton className="h-5 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Role check
  if (user.role !== "consultant") {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 dark:border-emerald-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <ConsultantDashboardHeader
        user={user}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ConsultantDashboardErrorBanner
          error={error}
          onRetry={loadRealData}
        />

        <ConsultantDashboardEmptyState
          show={!loading && !error && sessions.length === 0}
          onStartNewSession={handleStartNewSession}
        />

        <ConsultantDashboardStats stats={stats} />

        <ConsultantDashboardPendingRequestsBanner
          pendingRequests={stats.pending_requests}
          onViewRequests={() => {
            // Switch to assigned tab to show users with pending requests
            setActiveTab('assigned');
          }}
        />

        {/* Session Management */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Session Management</h2>
          </div>

          <ConsultantDashboardSessionTabs
            activeTab={activeTab}
            onChange={setActiveTab}
            tabCounts={{
              active: stats.active_sessions,
              completed: completedUsers.length,
              assigned: stats.assigned_users,
              engagements: sessions.filter(s => 
                s.status === 'completed' && 
                s.assessmentId && 
                (s.isConsultantLed === true || s.source === 'live_session')
              ).length,
              scheduled: sessions.filter(s => 
                s.status === 'pending'  // Only user-requested sessions
              ).length
            }}
          />

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {activeTab === 'assigned' ? (
              <AssignedUsersTab
                assignedUsers={assignedUsers}
                handleResumeSession={handleResumeSession}
                handleStartSessionClick={handleStartSessionClick}
                openingModal={openingModal}
                openingModalUserId={openingModalUserId}
              />
            ) : activeTab === 'engagements' ? (
              <EngagementsTab
                sessions={sessions}
                handleViewResults={handleViewResults}
                getStatusBadge={getStatusBadge}
                formatTime={formatTime}
              />
            ) : activeTab === 'completed' ? (
              <CompletedUsersTab
                completedUsers={completedUsers}
                expandedUser={expandedUser}
                toggleUserAssessments={toggleUserAssessments}
                loadingAssessments={loadingAssessments}
                userAssessments={userAssessments}
              />
            ) : (
              <ActiveSessionsTab
                sessions={sessions}
                activeTab={activeTab}
                handleResumeSession={handleResumeSession}
                handleViewResults={handleViewResults}
                handleStartNewSession={handleStartNewSession}
                getStatusBadge={getStatusBadge}
                formatTime={formatTime}
              />
            )}
          </div>
        </div>

      </div>

      {/* Session Start Modal */}
      {showSessionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Start New Assessment Session
                </h2>
                <button
                  onClick={() => setShowSessionModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="px-6 py-4 space-y-4">
              {selectedUser && (
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <h3 className="font-medium text-blue-900 dark:text-blue-300 mb-2">Client Information</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-blue-700 dark:text-blue-400 font-medium">Name:</span>
                      <span className="ml-2 text-blue-900 dark:text-blue-200">{selectedUser.name}</span>
                    </div>
                    <div>
                      <span className="text-blue-700 dark:text-blue-400 font-medium">Email:</span>
                      <span className="ml-2 text-blue-900 dark:text-blue-200">{selectedUser.email}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Assessment Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Assessment Type
                  {(selectedUser?.has_pending_request || selectedUser?.has_pending_session) && selectedUser?.pending_session?.assessment_type && (
                    <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                      (Requested by user)
                    </span>
                  )}
                </label>
                <select
                  value={selectedAssessmentType}
                  onChange={(e) => {
                    const hasPending = selectedUser?.has_pending_request || selectedUser?.has_pending_session;
                    if (!hasPending || !selectedUser?.pending_session?.assessment_type) {
                      setSelectedAssessmentType(e.target.value);
                    }
                  }}
                  disabled={(() => {
                    const hasPending = selectedUser?.has_pending_request || selectedUser?.has_pending_session;
                    return hasPending && selectedUser?.pending_session?.assessment_type;
                  })()}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    (() => {
                      const hasPending = selectedUser?.has_pending_request || selectedUser?.has_pending_session;
                      return hasPending && selectedUser?.pending_session?.assessment_type
                        ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-800'
                        : '';
                    })()
                  }`}
                >
                  <option value="ESA">ESA (Energy Savings Assessment)</option>
                  <option value="EMHA">EMHA (Energy Management Health Assessment)</option>
                </select>
                {(selectedUser?.has_pending_request || selectedUser?.has_pending_session) && selectedUser?.pending_session?.assessment_type && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Assessment type is locked based on the user's request
                  </p>
                )}
              </div>

              {/* Industry Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Major Industry Sector *
                </label>
                <select
                  value={selectedMajorIndustryObj 
                    ? `${selectedMajorIndustryObj.id || selectedMajorIndustry}-${selectedMajorIndustryObj.region || ''}` 
                    : (selectedMajorIndustry ? `${selectedMajorIndustry}-naics` : '')}
                  onChange={handleMajorIndustryChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  disabled={industriesLoading}
                >
                  <option value="">Select major industry sector...</option>
                  {majorIndustries.map((industry) => {
                    const industryName = typeof industry === 'string' ? industry : industry.name;
                    const region = typeof industry === 'string' ? '' : (industry.region || '');
                    const industryId = typeof industry === 'string' ? '' : (industry.id || '');
                    const displayName = region ? `${industryName} (${region.toUpperCase()})` : industryName;
                    // Use a unique key combining ID and region to ensure uniqueness
                    const uniqueKey = industryId ? `${industryId}-${region}` : `${industryName}-${region}`;
                    return (
                      <option key={uniqueKey} value={uniqueKey} data-region={region} data-name={industryName} data-id={industryId}>
                        {displayName}
                      </option>
                    );
                  })}
                </select>
              </div>

              {selectedMajorIndustry && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sub-Industry Specialization *
                  </label>
                  <select
                    value={selectedSubIndustry}
                    onChange={handleSubIndustryChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select sub-industry...</option>
                    {availableSubIndustries.map((subIndustry) => (
                      <option key={subIndustry} value={subIndustry}>{subIndustry}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Company Information */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter company name"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Contact Name *
                  </label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Enter contact name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Enter email address"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  State/Province *{' '}
                  <span className="text-xs text-blue-600 dark:text-blue-400">
                    (for grants matching)
                  </span>
                </label>
                <select
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select state/province...</option>
                  <option value="ACT">Australian Capital Territory</option>
                  <option value="NSW">New South Wales</option>
                  <option value="NT">Northern Territory</option>
                  <option value="QLD">Queensland</option>
                  <option value="SA">South Australia</option>
                  <option value="TAS">Tasmania</option>
                  <option value="VIC">Victoria</option>
                  <option value="WA">Western Australia</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Annual Energy Spend ($)
                  </label>
                  <input
                    type="number"
                    value={annualEnergySpend}
                    onChange={(e) => setAnnualEnergySpend(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Facility Size (sq ft)
                  </label>
                  <input
                    type="text"
                    value={facilitySize}
                    onChange={(e) => setFacilitySize(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Optional"
                  />
                </div>
              </div>

              {/* Session Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Session Notes
                </label>
                <textarea
                  value={sessionNotes}
                  onChange={(e) => setSessionNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Optional notes about this session..."
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowSessionModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                disabled={startingSession}
              >
                Cancel
              </button>
              <button
                onClick={handleStartSession}
                disabled={startingSession || !selectedMajorIndustry || !selectedSubIndustry || !(companyName || '').trim() || !(contactName || '').trim() || !(contactEmail || '').trim() || !(state || '').trim()}
                className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
              >
                {startingSession ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Starting...
                  </>
                ) : (
                  'Start Session'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toaster removed - using global Toaster from App.js */}
    </div>
  );
};

export default ConsultantDashboard;
